import { Row, Col, Button, Table, Nav, Form } from "react-bootstrap";
import useSWR, { mutate } from "swr";
import AuthenticatedWrapper from "../../components/wrapper";
import { fetcher, serverUrl, swrConfig, uploadPresignedFile } from "../../helpers/utility";
import { useCallback, useState, useRef } from "react";
import axios from "axios";
import { format, isToday } from "date-fns";
import NavigationBack from "../../components/NavigationBack";
import BasicModal from "../../components/BasicModal";
import InputWithLabel from "../../components/inputWithLabel";
import InputReadOnly from "../../components/inputReadOnly";
import { useForm } from "react-hook-form";

const S3_BUCKET_URL = "https://bplacedprivate.s3.eu-west-2.amazonaws.com";

const AGE_GROUP_OPTIONS = ["0-5", "5-10", "10-16", "16-18", "18+", "All ages"];
const DELIVERY_METHOD_OPTIONS = [
  { value: "in-person", label: "In-Person" },
  { value: "online", label: "Online" },
  { value: "both", label: "Both" },
];
const SERVICE_TYPE_OPTIONS = [
  "Legal Support",
  "Training",
  "Transport",
  "Therapeutic Services",
  "Independent Reviewing",
  "Assessment Services",
  "Family Support",
  "Educational Support",
  "Placement Support",
  "Other",
];

function AdminService({ user }) {
  const [modalConfig, setModalConfig] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceImage, setServiceImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  if (user?.staffUser === false) {
    window.location.href = "https://www.bplaced.co.uk";
  }

  // Fetch all services including inactive for admin management
  const { data: serviceData } = useSWR(`${serverUrl}/service?includeInactive=true`, fetcher, swrConfig);

  const allServices = useCallback(() => {
    if (serviceData && serviceData.services?.length > 0) {
      return serviceData.services;
    }
    return [];
  }, [serviceData]);

  // Toggle service active status
  const toggleServiceActive = async (service, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${serverUrl}/service`, {
        _id: service._id,
        active: !service.active,
      });
      await mutate(`${serverUrl}/service?includeInactive=true`);
    } catch (err) {
      console.error("Error toggling service status:", err);
    }
  };

  const showService = useCallback(({ service }) => {
    setModalConfig({
      title: service?.businessName,
      message: (
        <div>
          {service?.imageUrl?.externalId && (
            <div style={{ marginBottom: 15, textAlign: "center" }}>
              <img
                src={`${S3_BUCKET_URL}/${service.imageUrl.externalId}`}
                alt={service.businessName}
                style={{
                  maxWidth: 200,
                  maxHeight: 200,
                  borderRadius: 8,
                  objectFit: "cover",
                  border: "1px solid #dee2e6",
                }}
              />
            </div>
          )}
          <InputReadOnly label="Business Name" type="text" value={service?.businessName} />
          <InputReadOnly label="Contact Email" type="text" value={service?.contactEmail || "N/A"} />
          <InputReadOnly label="Contact Phone" type="text" value={service?.contactPhone || "N/A"} />
          <InputReadOnly label="Website" type="text" value={service?.websiteLink || "N/A"} />
          <InputReadOnly
            label="Delivery Method"
            type="text"
            value={
              service?.deliveryMethod === "in-person"
                ? "In-Person"
                : service?.deliveryMethod === "online"
                ? "Online"
                : "Both"
            }
          />
          {service?.location?.townCity && (
            <InputReadOnly
              label="Location"
              type="text"
              value={`${service?.location?.addressLine1 || ""} ${
                service?.location?.townCity || ""
              } ${service?.location?.postcode || ""}`.trim()}
            />
          )}
          <InputReadOnly label="Description" type="text" value={service?.shortDescription || "N/A"} />
          <InputReadOnly label="Service Type" type="text" value={service?.serviceType || "N/A"} />
          <InputReadOnly
            label="Target Age Groups"
            type="text"
            value={service?.targetAgeGroup?.join(", ") || "N/A"}
          />
          <InputReadOnly
            label="Created"
            type="text"
            value={service?.created ? format(new Date(service.created), "dd/MM/yyyy") : "N/A"}
          />
        </div>
      ),
      show: showModal,
      close: hideModal,
      size: "lg",
      buttons: [
        {
          title: "Delete",
          variant: "outline-danger",
          onClick: async () => {
            await axios.delete(`${serverUrl}/service/${service?._id}`);
            await mutate(`${serverUrl}/service?includeInactive=true`);
            hideModal();
          },
        },
        {
          title: "Close",
          variant: "outline-primary",
          onClick: () => {
            hideModal();
          },
        },
      ],
    });
    setShowModal(true);
  }, []);

  function hideModal() {
    setShowModal(false);
    setModalConfig(null);
  }

  function hideFormModal() {
    setShowFormModal(false);
    setEditingService(null);
    setServiceImage(null);
    setImagePreview(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function openAddModal() {
    setEditingService(null);
    setServiceImage(null);
    setImagePreview(null);
    reset();
    setShowFormModal(true);
  }

  function openEditModal(service) {
    setEditingService(service);
    
    // Pre-populate form with service data
    setValue("businessName", service.businessName || "");
    setValue("contactEmail", service.contactEmail || "");
    setValue("contactPhone", service.contactPhone || "");
    setValue("websiteLink", service.websiteLink || "");
    setValue("deliveryMethod", service.deliveryMethod || "both");
    setValue("serviceType", service.serviceType || "");
    setValue("addressLine1", service.location?.addressLine1 || "");
    setValue("addressLine2", service.location?.addressLine2 || "");
    setValue("townCity", service.location?.townCity || "");
    setValue("county", service.location?.county || "");
    setValue("postcode", service.location?.postcode || "");
    setValue("country", service.location?.country || "");
    setValue("shortDescription", service.shortDescription || "");
    
    // Set age group checkboxes
    AGE_GROUP_OPTIONS.forEach((age) => {
      setValue(`age_${age}`, service.targetAgeGroup?.includes(age) || false);
    });

    // Set existing image preview
    if (service.imageUrl?.externalId) {
      setImagePreview(`${S3_BUCKET_URL}/${service.imageUrl.externalId}`);
      setServiceImage(service.imageUrl);
    } else {
      setImagePreview(null);
      setServiceImage(null);
    }

    setShowFormModal(true);
  }

  // Handle image file selection
  function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be less than 5MB.");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to S3
    uploadImage(file);
  }

  // Upload image to S3
  async function uploadImage(file) {
    setIsUploading(true);

    const fileMeta = {
      name: file.name,
      size: file.size,
      type: file.type,
      acl: "public-read",
      folder: "services",
    };

    if (file.lastModified) {
      fileMeta.lastModified = new Date(file.lastModified);
    }

    try {
      const res = await axios.post(`${serverUrl}/fileUpload/presignedUrl`, fileMeta);

      if (res?.data?.key && res?.data?.contentType && res?.data?.url) {
        uploadPresignedFile({
          presignedurl: res.data.url,
          file: file,
          acl: fileMeta.acl,
          onSuccess: () => {
            fileMeta.externalId = res.data.key;
            fileMeta.contentType = res.data.contentType;
            setServiceImage(fileMeta);
            setIsUploading(false);
          },
          onFail: () => {
            alert("Failed to upload image. Please try again.");
            setIsUploading(false);
          },
          onProgress: null,
        });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      if (error?.response?.data?.errorCode === "TOO_LARGE") {
        alert("Image is too large. Maximum size is 5MB.");
      } else {
        alert("Failed to upload image. Please try again.");
      }
      setIsUploading(false);
    }
  }

  function removeImage() {
    setServiceImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const onSubmit = async (data) => {
    if (isUploading) {
      alert("Please wait for the image to finish uploading.");
      return;
    }

    try {
      // Process targetAgeGroup from checkboxes
      const targetAgeGroup = AGE_GROUP_OPTIONS.filter((age) => data[`age_${age}`]);

      const servicePayload = {
        businessName: data.businessName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        websiteLink: data.websiteLink,
        deliveryMethod: data.deliveryMethod,
        location: {
          addressLine1: data.addressLine1,
          addressLine2: data.addressLine2,
          townCity: data.townCity,
          county: data.county,
          postcode: data.postcode,
          country: data.country,
        },
        shortDescription: data.shortDescription,
        targetAgeGroup,
        serviceType: data.serviceType,
        imageUrl: serviceImage,
      };

      if (editingService) {
        // Update existing service
        await axios.put(`${serverUrl}/service`, {
          _id: editingService._id,
          ...servicePayload,
        });
      } else {
        // Create new service
        await axios.post(`${serverUrl}/service`, servicePayload);
      }
      
      await mutate(`${serverUrl}/service?includeInactive=true`);
      hideFormModal();
    } catch (err) {
      console.error("Error saving service:", err);
    }
  };

  return (
    <AuthenticatedWrapper user={user}>
      <NavigationBack />

      <div style={{ marginTop: "0px", padding: "10px 0px 15px" }}>
        <Row style={{ marginBottom: 15 }}>
          <Col>
            <Button variant="primary" onClick={openAddModal}>
              <i className="bi bi-plus-lg me-2"></i>
              Add Service
            </Button>
          </Col>
        </Row>

        <Row style={{ marginTop: 15 }}>
          <Col lg={12}>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Business Name</th>
                  <th>Contact Email</th>
                  <th>Phone</th>
                  <th>Delivery</th>
                  <th>Service Type</th>
                  <th style={{ width: "80px", textAlign: "center" }}>Active</th>
                  <th>Created</th>
                  <th style={{ width: "80px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allServices().map((service) => {
                  return (
                    <tr
                      key={service._id}
                      onClick={() => {
                        showService({ service });
                      }}
                      style={{
                        cursor: "pointer",
                        opacity: service.active ? 1 : 0.6,
                      }}
                    >
                      <td>{service.businessName}</td>
                      <td>{service.contactEmail || "-"}</td>
                      <td>{service.contactPhone || "-"}</td>
                      <td>
                        {service.deliveryMethod === "in-person"
                          ? "In-Person"
                          : service.deliveryMethod === "online"
                          ? "Online"
                          : "Both"}
                      </td>
                      <td>{service.serviceType || "-"}</td>
                      <td style={{ textAlign: "center" }}>
                        <Form.Check
                          type="switch"
                          id={`active-switch-${service._id}`}
                          checked={service.active}
                          onChange={(e) => toggleServiceActive(service, e)}
                          onClick={(e) => e.stopPropagation()}
                          title={service.active ? "Click to deactivate" : "Click to activate"}
                        />
                      </td>
                      <td>
                        {isToday(new Date(service.created))
                          ? "Today"
                          : format(new Date(service.created), "dd/MM/yyyy")}
                      </td>
                      <td style={{ textAlign: "center" }}>
                          <i
                            style={{
                              fontSize: "14px",
                              color: "green",
                              padding: "3px",
                              marginRight: "6px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(service);
                            }}
                            title="Edit service"
                            className="bi bi-pencil"
                          ></i>
                      </td>
                    </tr>
                  );
                })}

                {allServices().length === 0 && (
                  <tr>
                    <td colSpan={8}>No services found. Click "Add Service" to create one.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </div>

      {/* View Service Modal */}
      <BasicModal
        title={modalConfig?.title}
        message={modalConfig?.message}
        show={showModal}
        close={hideModal}
        size={modalConfig?.size || "lg"}
        buttons={modalConfig?.buttons}
      />

      {/* Add/Edit Service Modal */}
      <BasicModal
        title={editingService ? "Edit Service" : "Add New Service"}
        message={
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={6}>
                <InputWithLabel
                  inputId="businessName"
                  label="Business Name"
                  placeholder="Enter business name"
                  register={register}
                  validation={{ required: true }}
                  error={errors.businessName}
                  errorMessage="Business name is required"
                />
              </Col>
              <Col md={6}>
                <InputWithLabel
                  inputId="contactEmail"
                  label="Contact Email"
                  type="email"
                  placeholder="Enter contact email"
                  register={register}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <InputWithLabel
                  inputId="contactPhone"
                  label="Main Contact (Phone)"
                  placeholder="Enter phone number"
                  register={register}
                />
              </Col>
              <Col md={6}>
                <InputWithLabel
                  inputId="websiteLink"
                  label="Website/Contact Link"
                  placeholder="Enter website URL"
                  register={register}
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Delivery Method</label>
                  <Form.Select {...register("deliveryMethod")}>
                    {DELIVERY_METHOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Service Type</label>
                  <Form.Select {...register("serviceType")}>
                    <option value="">Select service type</option>
                    {SERVICE_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Col>
            </Row>

            <hr />
            <h6 className="mb-3">Location (if applicable)</h6>

            <Row>
              <Col md={6}>
                <InputWithLabel
                  inputId="addressLine1"
                  label="Address Line 1"
                  placeholder="Enter address line 1"
                  register={register}
                />
              </Col>
              <Col md={6}>
                <InputWithLabel
                  inputId="addressLine2"
                  label="Address Line 2"
                  placeholder="Enter address line 2"
                  register={register}
                />
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <InputWithLabel
                  inputId="townCity"
                  label="Town/City"
                  placeholder="Enter town or city"
                  register={register}
                />
              </Col>
              <Col md={4}>
                <InputWithLabel
                  inputId="county"
                  label="County"
                  placeholder="Enter county"
                  register={register}
                />
              </Col>
              <Col md={4}>
                <InputWithLabel
                  inputId="postcode"
                  label="Postcode"
                  placeholder="Enter postcode"
                  register={register}
                />
              </Col>
            </Row>

            <hr />

            <InputWithLabel
              inputId="shortDescription"
              label="Short Description"
              type="textarea"
              placeholder="Enter a brief description of the service"
              register={register}
              inputProps={{ rows: 3 }}
            />

            <div className="mb-3">
              <label className="form-label">Target Age Group</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                {AGE_GROUP_OPTIONS.map((age) => (
                  <Form.Check
                    key={age}
                    type="checkbox"
                    id={`age_${age}`}
                    label={age}
                    {...register(`age_${age}`)}
                  />
                ))}
              </div>
            </div>

            <hr />
            <h6 className="mb-3">Service Image/Logo</h6>

            <div className="mb-3">
              {imagePreview ? (
                <div style={{ marginBottom: 15 }}>
                  <div
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #dee2e6",
                      marginBottom: 10,
                    }}
                  >
                    <img
                      src={imagePreview}
                      alt="Service preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={removeImage}
                    disabled={isUploading}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Remove Image
                  </Button>
                </div>
              ) : (
                <div
                  style={{
                    width: 150,
                    height: 150,
                    borderRadius: 8,
                    border: "2px dashed #dee2e6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 10,
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <span style={{ color: "#6c757d", fontSize: 13 }}>No image</span>
                </div>
              )}

              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                  id="serviceImageInput"
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      {imagePreview ? "Change Image" : "Upload Image"}
                    </>
                  )}
                </Button>
              </div>
              <small className="text-muted d-block mt-2">
                Recommended: Square image, max 5MB. JPG, PNG, or GIF.
              </small>
            </div>
          </Form>
        }
        show={showFormModal}
        close={hideFormModal}
        size="lg"
        buttons={[
          {
            title: "Cancel",
            variant: "outline-secondary",
            onClick: hideFormModal,
          },
          {
            title: editingService ? "Save Changes" : "Add Service",
            variant: "primary",
            onClick: handleSubmit(onSubmit),
          },
        ]}
      />
    </AuthenticatedWrapper>
  );
}

export default AdminService;

